import { Link } from "react-router-dom";
import {
  Shield, Sparkles, ArrowRight, CheckCircle2, FileText, Users, Building2,
  ClipboardCheck, Vote, Layers, Plug, MessageSquare, Radar, Handshake, Wrench,
  MapPin, Lock, ScrollText, Home,
} from "lucide-react";
import Wordmark from "@/components/Wordmark";
import { Button } from "@/components/ui/button";

// Shared building blocks --------------------------------------------------

const PILLARS = [
  {
    icon: Shield,
    title: "Liability shield built in",
    body:
      "Every regulated action passes through an immutable Confirmation Gate. Legislation versions are pinned. AI classifies intent and routes legal-style questions to a professional — never gives advice it isn't licensed to give.",
    image: "/images/feature-liability-shield.png",
  },
  {
    icon: Sparkles,
    title: "AI that actually knows your data",
    body:
      "OPSTRAIT reads your levies, meetings, compliance items, and documents. Ask \"when is my next levy due\" or \"who's RSVP'd to the AGM\" and get a real answer — sourced, not hallucinated. Legal questions get routed. Data stays scoped to your scheme.",
    image: "/images/feature-ai-copilot.png",
  },
  {
    icon: MapPin,
    title: "Multi-jurisdiction from day one",
    body:
      "Full coverage across all 8 Australian jurisdictions — NSW · VIC · QLD · WA · SA · TAS · ACT · NT. Legislation versions and regulation modules per state. Compliance templates adapt automatically.",
    image: null,
  },
];

const PERSONA_PILLS = [
  { icon: Home, name: "Owners", role: "Track levies, meetings, by-laws, and documents." },
  { icon: Users, name: "Committees", role: "Vote motions, gate risky writes, keep the audit trail." },
  { icon: ClipboardCheck, name: "Chairs", role: "Draft minutes, run notices, protect the committee." },
  { icon: Building2, name: "Strata Managers", role: "Manage a portfolio of schemes in one workspace." },
  { icon: Handshake, name: "Insurance Brokers", role: "Receive scoped quote requests with disclosures." },
  { icon: Wrench, name: "Service Providers", role: "Respond to lead pipelines from participating schemes." },
];

const FEATURES = [
  { icon: Home, name: "Owner Portal", blurb: "Levies, meetings, documents, messages — one owner view per lot." },
  { icon: Vote, name: "Meeting Engine", blurb: "Notice, agenda, motions, entitlement voting, AI-drafted minutes." },
  { icon: Radar, name: "Compliance Radar", blurb: "Jurisdiction-aware templates, expiry tracking, weekly digests." },
  { icon: Shield, name: "Insurance Command", blurb: "Policy tracking, broker cross-linking, renewal alerts." },
  { icon: ScrollText, name: "By-law Register", blurb: "Draft, propose, gate, publish — state-adapted language." },
  { icon: Plug, name: "Connector Registry", blurb: "Gmail, Calendar, Drive, Outlook, WhatsApp, Stripe — plug in the tools you already use." },
];

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

// Book-a-demo — plain mailto placeholder; wire modal later.
const DEMO_MAILTO =
  "mailto:hello@opstrait.com?subject=Book%20a%20demo%20—%20OPSTRAIT&body=Hi%20—%20I'd%20like%20to%20see%20OPSTRAIT.%20%0A%0AName%3A%20%0AScheme%20/%20role%3A%20%0AState%3A%20%0AAny%20context%3A%20";

// Nav --------------------------------------------------------------------

function TopNav() {
  return (
    <header
      className="sticky top-0 z-40 border-b backdrop-blur-md bg-white/85"
      style={{ borderColor: "var(--border)" }}
      data-testid="landing-top-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Wordmark size={28} />
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <a href="#product" className="hover:text-slate-950 transition-colors">Product</a>
            <a href="#for-whom" className="hover:text-slate-950 transition-colors">For whom</a>
            <a href="#trust" className="hover:text-slate-950 transition-colors">Trust</a>
            <a href="#pricing" className="hover:text-slate-950 transition-colors">Pricing</a>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login">
            <Button variant="ghost" size="sm" className="font-medium" data-testid="nav-signin-btn">
              Sign in
            </Button>
          </Link>
          <a href={DEMO_MAILTO}>
            <Button
              size="sm"
              className="font-semibold"
              style={{ background: "var(--navy)", color: "white" }}
              data-testid="nav-demo-btn"
            >
              Book a demo
            </Button>
          </a>
        </div>
      </div>
    </header>
  );
}

// Hero -------------------------------------------------------------------

function Hero() {
  return (
    <section className="border-b bg-white" style={{ borderColor: "var(--border)" }} data-testid="landing-hero">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 min-h-[600px] grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <span
            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-6"
            style={{ color: "var(--blue-dark)" }}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--blue)" }} />
            The operations platform for Australian strata
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            style={{ color: "var(--navy)" }}
            data-testid="landing-h1"
          >
            The AI co-pilot for self-managed strata committees.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-slate-600 max-w-xl">
            OPSTRAIT is the operations platform for Australian strata committees,
            brokers, and self-managing owners. Compliance, meetings, levies,
            insurance, and owner portals — with an immutable audit trail and AI
            that knows when to route to a professional.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={DEMO_MAILTO}>
              <Button
                size="lg"
                className="font-semibold px-6"
                style={{ background: "var(--navy)", color: "white" }}
                data-testid="hero-demo-btn"
              >
                Book a demo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/login">
              <Button size="lg" variant="ghost" className="font-medium px-6" data-testid="hero-signin-btn">
                Sign in
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--blue)" }} /> Built for all 8 Australian jurisdictions</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--blue)" }} /> Liability-shift framework</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--blue)" }} /> Immutable audit trail</span>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div
            className="relative rounded-2xl overflow-hidden border shadow-2xl"
            style={{ borderColor: "var(--border)", boxShadow: "0 25px 50px -20px rgba(15,23,42,0.25)" }}
          >
            <img
              src="/images/landing-hero.png"
              alt="OPSTRAIT — AI co-pilot for strata committees"
              className="w-full h-auto"
              data-testid="hero-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// Persona strip ----------------------------------------------------------

function PersonaStrip() {
  return (
    <section id="for-whom" className="border-b bg-slate-50/60" style={{ borderColor: "var(--border)" }} data-testid="persona-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ color: "var(--navy)" }}>
          Built for the whole strata ecosystem
        </h2>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          One platform. Six personas. Everyone sees what they need — and nothing they don&apos;t.
        </p>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {PERSONA_PILLS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.name}
                className="group rounded-xl border bg-white p-4 hover:border-[color:var(--blue)] transition-colors"
                style={{ borderColor: "var(--border)" }}
                data-testid={`persona-pill-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="w-5 h-5 mb-2" style={{ color: "var(--blue)" }} />
                <div className="text-sm font-semibold" style={{ color: "var(--navy)" }}>{p.name}</div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">{p.role}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Pillars ----------------------------------------------------------------

function Pillars() {
  return (
    <section id="product" className="border-b bg-white" style={{ borderColor: "var(--border)" }} data-testid="pillars">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="max-w-2xl mb-12">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--navy)" }}>
            A platform that respects the seriousness of strata operations.
          </h2>
          <p className="mt-3 text-slate-600">
            OPSTRAIT sits between AI convenience and legal accountability. Every
            capability is designed with the assumption that a real regulator
            might one day look at the audit trail.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, idx) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="rounded-2xl border bg-white p-6 flex flex-col"
                style={{ borderColor: "var(--border)" }}
                data-testid={`pillar-${idx}`}
              >
                {p.image ? (
                  <div className="rounded-lg overflow-hidden mb-4 border" style={{ borderColor: "var(--border)" }}>
                    <img src={p.image} alt={p.title} className="w-full h-40 object-cover" />
                  </div>
                ) : (
                  <div className="rounded-lg mb-4 p-4 bg-slate-50 border" style={{ borderColor: "var(--border)" }}>
                    <div className="grid grid-cols-4 gap-1.5">
                      {STATES.map((s) => (
                        <div
                          key={s}
                          className="text-[10px] font-semibold px-2 py-1.5 rounded text-center"
                          style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <Icon className="w-6 h-6 mb-3" style={{ color: "var(--blue)" }} />
                <h3 className="text-lg font-semibold" style={{ color: "var(--navy)" }}>{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Product surface tour ---------------------------------------------------

function ProductTour() {
  return (
    <section className="border-b bg-slate-50/60" style={{ borderColor: "var(--border)" }} data-testid="product-tour">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="max-w-2xl mb-10">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--navy)" }}>
            The full operating surface.
          </h2>
          <p className="mt-3 text-slate-600">Six purpose-built modules — designed for how strata actually works.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.name}
                className="rounded-xl border bg-white p-5 hover:shadow-md transition-shadow"
                style={{ borderColor: "var(--border)" }}
                data-testid={`feature-${f.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="w-5 h-5 mb-3" style={{ color: "var(--blue)" }} />
                <div className="text-base font-semibold" style={{ color: "var(--navy)" }}>{f.name}</div>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.blurb}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// How it works -----------------------------------------------------------

function HowItWorks() {
  const steps = [
    { n: 1, title: "Connect", body: "Onboard your scheme in 8 steps: legal structure, lots, committee, integrations." },
    { n: 2, title: "Operate", body: "AI-drafted minutes, gated regulated actions, immutable audit trail." },
    { n: 3, title: "Prove", body: "Every action has a signed confirmation, tied to the legislation version in force. Auditors love it." },
  ];
  return (
    <section className="border-b bg-white" style={{ borderColor: "var(--border)" }} data-testid="how-it-works">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-10" style={{ color: "var(--navy)" }}>
          Three steps to compliance-grade operations.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="rounded-xl border p-6 bg-white" style={{ borderColor: "var(--border)" }} data-testid={`how-step-${s.n}`}>
              <div className="text-4xl font-bold tabular-nums" style={{ color: "var(--blue)" }}>0{s.n}</div>
              <div className="mt-2 text-lg font-semibold" style={{ color: "var(--navy)" }}>{s.title}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Trust ------------------------------------------------------------------

function Trust() {
  const rows = [
    { icon: ScrollText, title: "Not a lawyer, and we tell you so.", body: "An intent classifier evaluates every question. Legal-style asks are routed to a professional — with the disclaimer visible in the response body." },
    { icon: Lock, title: "Every write leaves a signed trail.", body: "Regulated actions land in the immutable confirmations collection, pinned to the exact legislation version in force at the time of the action." },
    { icon: Shield, title: "Your data stays yours.", body: "Australian data residency. Scheme-scoped access. Encrypted OAuth tokens for every connector. Minimum-scope integrations by design." },
    { icon: CheckCircle2, title: "You choose when to go live.", body: "Every jurisdiction requires an explicit legal sign-off before regulated actions can be executed. Preview mode is the default — no accidental liability." },
  ];
  return (
    <section id="trust" className="border-b bg-white" style={{ borderColor: "var(--border)" }} data-testid="trust">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--navy)" }}>
          Trust &amp; guardrails.
        </h2>
        <p className="mt-2 text-slate-600 max-w-2xl">Legally-aware software for people who don&apos;t want their volunteer role to become a legal liability.</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {rows.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.title} className="flex gap-4 rounded-xl border bg-white p-6" style={{ borderColor: "var(--border)" }}>
                <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "var(--blue-light)" }}>
                  <Icon className="w-5 h-5" style={{ color: "var(--blue-dark)" }} />
                </div>
                <div>
                  <div className="text-base font-semibold" style={{ color: "var(--navy)" }}>{r.title}</div>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{r.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Pricing ----------------------------------------------------------------

function Pricing() {
  const tiers = [
    {
      name: "Solo Committee",
      target: "Small self-managed scheme",
      price: "$—", cadence: "/ scheme / month — from launch",
      features: ["Owner portal", "Compliance radar", "Meeting engine", "AI Q&A (fair-use)", "1 connector"],
      cta: "Contact us",
    },
    {
      name: "Multi-Scheme",
      target: "Broker or manager portfolio",
      price: "$—", cadence: "/ scheme / month — from launch",
      features: ["Everything in Solo", "Portfolio dashboard", "Insurance Command", "SP marketplace access", "All connectors"],
      cta: "Contact us", highlight: true,
    },
    {
      name: "Enterprise",
      target: "Strata management firm",
      price: "Custom", cadence: "",
      features: ["Everything in Multi-Scheme", "SSO / SAML", "Custom compliance templates", "White-label option", "Dedicated success manager"],
      cta: "Contact us",
    },
  ];
  return (
    <section id="pricing" className="border-b bg-slate-50/60" style={{ borderColor: "var(--border)" }} data-testid="pricing">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--navy)" }}>Pricing.</h2>
        <p className="mt-2 text-slate-600">Contact us for pilot pricing. Final pricing published at general availability.</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t) => (
            <div
              key={t.name}
              className="rounded-2xl border p-7 flex flex-col bg-white"
              style={{
                borderColor: t.highlight ? "var(--blue)" : "var(--border)",
                boxShadow: t.highlight ? "0 12px 30px -12px rgba(37,99,235,0.35)" : "none",
              }}
              data-testid={`pricing-${t.name.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--blue)" }}>{t.target}</div>
              <div className="text-xl font-semibold mt-1" style={{ color: "var(--navy)" }}>{t.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-3xl font-bold tabular-nums" style={{ color: "var(--navy)" }}>{t.price}</span>
                <span className="text-xs text-slate-500">{t.cadence}</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--blue)" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a href={DEMO_MAILTO} className="mt-6">
                <Button
                  className="w-full font-semibold"
                  style={t.highlight
                    ? { background: "var(--navy)", color: "white" }
                    : { background: "white", color: "var(--navy)", border: "1px solid var(--border)" }}
                >
                  {t.cta}
                </Button>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Final CTA + Footer -----------------------------------------------------

function FinalCTA() {
  return (
    <section className="bg-slate-950 text-white" data-testid="final-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-center">
        <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight">Ready to try it?</h2>
        <p className="mt-3 text-slate-200 max-w-xl mx-auto">
          Book a 20-minute walk-through. We&apos;ll show you the audit trail, the AI classifier, and the compliance radar in your state.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <a href={DEMO_MAILTO}>
            <Button size="lg" className="font-semibold px-8" style={{ background: "var(--blue)", color: "white" }} data-testid="cta-demo-btn">
              Book a demo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
          <Link to="/login">
            <span className="text-sm text-slate-200 underline underline-offset-4 hover:text-white transition-colors">
              or sign in to your account
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t bg-white" style={{ borderColor: "var(--border)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-1">
          <Wordmark size={24} />
          <p className="mt-3 text-xs text-slate-600 leading-relaxed max-w-xs">
            Strata operations, powered by AI. Made for Australian self-managed committees.
          </p>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#product" className="text-slate-700 hover:text-slate-950">Overview</a></li>
            <li><a href="#for-whom" className="text-slate-700 hover:text-slate-950">For whom</a></li>
            <li><a href="#pricing" className="text-slate-700 hover:text-slate-950">Pricing</a></li>
            <li><Link to="/login" className="text-slate-700 hover:text-slate-950">Sign in</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><a href={DEMO_MAILTO} className="text-slate-700 hover:text-slate-950">Contact</a></li>
            <li><a href={DEMO_MAILTO} className="text-slate-700 hover:text-slate-950">Book a demo</a></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="text-slate-700 hover:text-slate-950">Privacy</Link></li>
            <li><Link to="/terms" className="text-slate-700 hover:text-slate-950">Terms</Link></li>
            <li><a href={DEMO_MAILTO} className="text-slate-700 hover:text-slate-950">Data handling</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between text-xs text-slate-600">
          <div>© {new Date().getFullYear()} OPSTRAIT. All rights reserved.</div>
          <div>Made in Australia.</div>
        </div>
      </div>
    </footer>
  );
}

// Page -------------------------------------------------------------------

export default function PublicLanding() {
  return (
    <div className="min-h-screen bg-white" data-testid="public-landing">
      {/* JSON-LD Organisation schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "OPSTRAIT",
            url: "https://opstrait.com/",
            logo: "https://opstrait.com/images/landing-hero.png",
            description:
              "The AI co-pilot for self-managed strata committees. Operations platform for Australian strata: compliance, meetings, levies, insurance, owner portals — with an immutable audit trail.",
            areaServed: "AU",
            sameAs: [],
          }),
        }}
      />
      <TopNav />
      <Hero />
      <PersonaStrip />
      <Pillars />
      <ProductTour />
      <HowItWorks />
      <Trust />
      <Pricing />
      <FinalCTA />
      <Footer />
    </div>
  );
}
